// Broker "My saved signature" — draw or upload once, reused on every E-Sign
// document where "I will also sign this document" is ticked.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PenLine, Upload, Trash2, Loader2, X, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BRAND = '#0057FF';

export default function MySignatureDialog({ headers, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(null);        // data URL of saved signature
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('draw');         // 'draw' | 'upload'
  const canvasRef = useRef(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/esign/my-signature`, { headers });
      if (data.has_signature) setSaved(data.signature);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Canvas drawing
  useEffect(() => {
    if (mode !== 'draw') return undefined;
    const c = canvasRef.current;
    if (!c) return undefined;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#0F172A';
    let drawing = false, last = null;
    const pos = (e) => { const r = c.getBoundingClientRect(); const p = e.touches ? e.touches[0] : e; return { x: (p.clientX - r.left) * (c.width / r.width), y: (p.clientY - r.top) * (c.height / r.height) }; };
    const start = (e) => { drawing = true; last = pos(e); e.preventDefault(); };
    const move = (e) => { if (!drawing) return; const p = pos(e); ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(p.x, p.y); ctx.stroke(); last = p; e.preventDefault(); };
    const stop = () => { drawing = false; };
    c.addEventListener('mousedown', start); c.addEventListener('mousemove', move); c.addEventListener('mouseup', stop); c.addEventListener('mouseleave', stop);
    c.addEventListener('touchstart', start, { passive: false }); c.addEventListener('touchmove', move, { passive: false }); c.addEventListener('touchend', stop);
    return () => { c.removeEventListener('mousedown', start); c.removeEventListener('mousemove', move); c.removeEventListener('mouseup', stop); c.removeEventListener('mouseleave', stop); c.removeEventListener('touchstart', start); c.removeEventListener('touchmove', move); c.removeEventListener('touchend', stop); };
  }, [mode, loading]);

  const clearCanvas = () => { const c = canvasRef.current; if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height); };
  const canvasEmpty = () => { const c = canvasRef.current; if (!c) return true; const px = c.getContext('2d').getImageData(0, 0, c.width, c.height).data; for (let i = 3; i < px.length; i += 4) if (px[i] !== 0) return false; return true; };

  const saveDataUrl = async (dataUrl) => {
    setBusy(true);
    try {
      await axios.put(`${API}/api/esign/my-signature`, { signature_data_url: dataUrl }, { headers });
      setSaved(dataUrl);
      toast.success('Signature saved — it will be used automatically when you self-sign');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save signature'); }
    finally { setBusy(false); }
  };

  const saveDrawing = () => { if (canvasEmpty()) { toast.error('Please draw your signature first'); return; } saveDataUrl(canvasRef.current.toDataURL('image/png')); };

  const onUpload = (e) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    if (!/png|jpe?g/i.test(f.type)) { toast.error('Use a PNG or JPG image'); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error('Image too large (max 2 MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      // Normalise to PNG via a canvas so the backend always receives PNG.
      const img = new Image();
      img.onload = () => {
        const cv = document.createElement('canvas');
        cv.width = img.width; cv.height = img.height;
        cv.getContext('2d').drawImage(img, 0, 0);
        saveDataUrl(cv.toDataURL('image/png'));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(f);
  };

  const remove = async () => {
    if (!window.confirm('Remove your saved signature?')) return;
    setBusy(true);
    try { await axios.delete(`${API}/api/esign/my-signature`, { headers }); setSaved(null); toast.success('Saved signature removed'); }
    catch { toast.error('Could not remove signature'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden" data-testid="esign-my-signature-dialog">
        <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">E-Sign</p>
            <p className="text-xl font-extrabold text-slate-900">My saved signature</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100" data-testid="esign-my-signature-close"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">Save your signature once. When you tick <span className="font-semibold">“I will also sign this document”</span>, it&apos;s applied to your fields automatically — you&apos;ll never redraw it. It is only ever used for fields assigned to you, never a client&apos;s.</p>

          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : saved ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="esign-my-signature-saved">
              <div className="flex items-center gap-2 text-emerald-800 mb-2"><CheckCircle2 className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-wider">Signature on file</span></div>
              <div className="rounded-xl bg-white border border-slate-200 p-3 flex items-center justify-center" style={{ minHeight: 120 }}>
                <img src={saved} alt="Your saved signature" className="max-h-28 object-contain" data-testid="esign-my-signature-preview" />
              </div>
              <div className="mt-3 flex items-center justify-between">
                <button type="button" onClick={() => setSaved(null)} className="text-xs font-bold" style={{ color: BRAND }} data-testid="esign-my-signature-replace">Replace signature</button>
                <button type="button" onClick={remove} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold text-rose-600" data-testid="esign-my-signature-delete"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-1.5">
                <button type="button" onClick={() => setMode('draw')} data-testid="esign-my-signature-mode-draw"
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold border ${mode === 'draw' ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200'}`}
                        style={mode === 'draw' ? { backgroundColor: BRAND } : {}}><PenLine className="w-3.5 h-3.5" /> Draw</button>
                <button type="button" onClick={() => setMode('upload')} data-testid="esign-my-signature-mode-upload"
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold border ${mode === 'upload' ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200'}`}
                        style={mode === 'upload' ? { backgroundColor: BRAND } : {}}><Upload className="w-3.5 h-3.5" /> Upload image</button>
              </div>

              {mode === 'draw' ? (
                <>
                  <div className="rounded-xl border-2 border-dashed border-slate-300 overflow-hidden bg-white" style={{ aspectRatio: '3 / 1' }}>
                    <canvas ref={canvasRef} width={900} height={300} data-testid="esign-my-signature-canvas" className="w-full h-full cursor-crosshair" style={{ touchAction: 'none' }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <button type="button" onClick={clearCanvas} className="text-xs font-bold text-slate-500 hover:text-slate-700">Clear</button>
                    <p className="text-[11px] text-slate-400">Use your finger, stylus or mouse</p>
                  </div>
                  <button type="button" onClick={saveDrawing} disabled={busy} data-testid="esign-my-signature-save"
                          className="w-full rounded-full text-sm font-bold py-3 text-white shadow-sm hover:opacity-90 disabled:opacity-50"
                          style={{ backgroundColor: BRAND }}>{busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save signature'}</button>
                </>
              ) : (
                <div onClick={() => fileRef.current?.click()} className="rounded-xl border-2 border-dashed border-slate-300 p-8 text-center cursor-pointer bg-slate-50 hover:bg-slate-100" data-testid="esign-my-signature-drop">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onUpload} data-testid="esign-my-signature-file" />
                  <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                  <p className="text-sm font-bold text-slate-900">Upload a signature image</p>
                  <p className="text-[11px] mt-0.5 text-slate-500">PNG or JPG · transparent background recommended · max 2 MB</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
